import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useSession } from './session';

/**
 * The company's own record — name, contact details, and how often payment
 * reminders repeat.
 *
 * RLS on `companies` allows SELECT to anyone in the company and UPDATE only to
 * an admin, so this file does not re-check the role. Hiding the screen is a
 * courtesy; the database is what actually refuses.
 */

export interface Company {
  id: string;
  name: string;
  legalName: string | null;
  gstNumber: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  /** Days between repeat reminders while a project still owes money. 1–365. */
  reminderDays: number;
}

export function useCompany() {
  const actor = useSession((s) => s.actor);
  return useQuery({
    queryKey: ['company', actor?.companyId ?? ''],
    enabled: !!actor,
    queryFn: async (): Promise<Company> => {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, legal_name, gst_number, phone, email, address, city, reminder_days')
        // RLS already limits this to one row, so there is nothing to filter on.
        .single();
      if (error) throw new Error(error.message);
      return {
        id: data.id,
        name: data.name,
        legalName: data.legal_name,
        gstNumber: data.gst_number,
        phone: data.phone,
        email: data.email,
        address: data.address,
        city: data.city,
        reminderDays: data.reminder_days,
      };
    },
  });
}

export interface CompanyFields {
  name: string;
  legalName: string;
  gstNumber: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  reminderDays: number;
}

export async function getCompanyDetails(): Promise<CompanyFields> {
  const defaults: CompanyFields = {
    name: 'VNS Contruction',
    legalName: '',
    gstNumber: '',
    phone: '+91 9659611044',
    email: 'vnsconstruction@hotmail.com',
    address: '90/14, VIP Thamarai Nagar, Kollampalayam',
    city: 'Erode-638002',
    reminderDays: 21,
  };

  try {
    const { data } = await supabase
      .from('companies')
      .select('name, legal_name, gst_number, phone, email, address, city, reminder_days')
      .maybeSingle();

    if (!data) return defaults;
    return {
      name: data.name || defaults.name,
      legalName: data.legal_name || defaults.legalName,
      gstNumber: data.gst_number || defaults.gstNumber,
      phone: data.phone || defaults.phone,
      email: data.email || defaults.email,
      address: data.address || defaults.address,
      city: data.city || defaults.city,
      reminderDays: data.reminder_days || defaults.reminderDays,
    };
  } catch {
    return defaults;
  }
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (v: CompanyFields) => {
      if (!actor) throw new Error('Not signed in');

      // The database has the same CHECK constraint. Catching it here turns a
      // Postgres constraint message into a sentence the user can act on.
      if (!Number.isInteger(v.reminderDays) || v.reminderDays < 1 || v.reminderDays > 365)
        throw new Error('Reminder interval must be between 1 and 365 days');
      if (!v.name.trim()) throw new Error('Company name is required');

      const { error } = await supabase
        .from('companies')
        .update({
          name: v.name.trim(),
          legal_name: v.legalName.trim() || null,
          gst_number: v.gstNumber.trim().toUpperCase() || null,
          phone: v.phone.trim() || null,
          email: v.email.trim() || null,
          address: v.address.trim() || null,
          city: v.city.trim() || null,
          reminder_days: v.reminderDays,
        })
        .eq('id', actor.companyId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company'] });
      // A shorter interval can make a reminder due immediately, so the bell
      // should not keep showing yesterday's answer.
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
